import React, { FC } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as ErrorMonitor from 'core/ErrorMonitor';

import FormGroup from 'components/form/FormGroup';
import Form from 'components/form/Form';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { setAuthTokensFromResponse, setUserInfo } from 'utils/auth';
import setServerErrors from 'utils/helpers/formErrors';
import { useWorkModeContext } from 'store/work-mode';
import { usePermissionsContext } from 'store/permissions';
import { useLanguageContext } from 'store/language';
import { useTwoFactorContext } from 'store/two-factor';

interface IProps {
  userInfo: {
    id: number;
    name: string;
    email: string;
    paidLeaveDays: number;
    paidLeaveDaysServedTime: number;
    paidLeaveDaysIrregularWorkingHours: number;
    paidLeaveDaysSpecificWorkingConditions: number;
    paidLeaveDaysAdditionalLeave: number;
    paidLeaveDaysByYear: [];
    wfhLimitType: string;
    remainingWfhLeavesPerWeek: number;
    remainingWfhLeavesPerMonth: number;
    featureLeavesIsActive: boolean;
    featureLeaveReview: boolean;
    featureLeavesHalfDayOption: boolean;
    featureForm76: boolean;
    featureColleaguesLeaves: boolean;
    featureExitInterview: boolean;
    featureSickLeaveWithoutAttachment: boolean;
    featureArt157LeaveWithoutAttachment: boolean;
    featureLeaveDaysServedTime: boolean;
    featureLeaveDaysIrregularWorkingHours: boolean;
    featureLeaveDaysSpecificWorkingConditions: boolean;
    featureLeaveDaysAdditionalLeave: boolean;
    featureHourlyAbsences: boolean;
    featureHealthAndSafetyTraining: boolean;
    featureWorkTrips: boolean;
    featureCustomFeatureForClientF: boolean;
    featureLimitWfhLeaveDays: boolean;
    featureShowWFHLeaveTypeInMainDropdown: boolean;
    featureLeaveSubstituteEmployee: boolean;
    featureRequireLeaveSubstituteEmployee: boolean;
    featureEmployeeCandidatesIsActive: boolean;
    featureGroupRoleLimitsIsActive: boolean;
    featureReminderForProbationPeriodEnd: boolean;
    featureLimitDocumentsToLeaves: boolean;
    reminderForIdCardExpiration: boolean;
    shouldDisallowLeaveDays: boolean;
    disallowedLeavesFrom: string;
    disallowedLeavesTo: string;
    isCandidate: boolean;
    employeeCandidateHelpText: string;
    totalHourlyAbsencesRemainingHours?: number;
    totalHourlyAbsencesUncoveredHours?: number;
    totalHourlyAbsencesHoursAboutToBeCovered?: number;
  };
}
interface IInputs {
  email: string;
  password: string;
  password_confirmation: string;
}

const ActivateAccountForm: FC<IProps> = ({ userInfo }: IProps) => {
  const { t } = useTranslation();
  const { setPermissions } = usePermissionsContext();
  const history = useHistory();
  const { setWorkMode, setWorkModeName, setAltWorkModeName } = useWorkModeContext();
  const { setLanguage } = useLanguageContext();
  const { setIs2FaComplete, setIs2FaEnabled } = useTwoFactorContext();
  const { confirmationToken } = useParams<{
    confirmationToken: string | undefined;
  }>();
  const methods = useForm<IInputs>({
    defaultValues: {
      email: userInfo.email,
    },
  });
  const onSubmit = (data: IInputs) => {
    return API.post('auth/account-activation', {
      confirmation_token: confirmationToken,
      password: data.password,
      password_confirmation: data.password_confirmation,
    })
      .then(async res => {
        const {
          continue_to_second_step: continueTo2FA,
          work_mode: workMode,
          work_mode_name: workModeName,
          alt_work_mode_name: altWorkModeName,
          lang,
        } = res.data;

        ErrorMonitor.setUser({
          id: userInfo.id.toString(),
          email: userInfo.email,
        });

        // Update tokens
        await setAuthTokensFromResponse(res);

        const permissions = await API.get('permissions');

        setPermissions(permissions.data);

        setUserInfo({
          id: userInfo.id,
          name: userInfo.name,
          email: userInfo.email,
          paidLeaveDays: userInfo.paidLeaveDays,
          paidLeaveDaysServedTime: userInfo.paidLeaveDaysServedTime,
          paidLeaveDaysIrregularWorkingHours: userInfo.paidLeaveDaysIrregularWorkingHours,
          paidLeaveDaysSpecificWorkingConditions: userInfo.paidLeaveDaysSpecificWorkingConditions,
          paidLeaveDaysAdditionalLeave: userInfo.paidLeaveDaysAdditionalLeave,
          paidLeaveDaysByYear: userInfo.paidLeaveDaysByYear,
          wfhLimitType: userInfo.wfhLimitType,
          remainingWfhLeavesPerWeek: userInfo.remainingWfhLeavesPerWeek,
          remainingWfhLeavesPerMonth: userInfo.remainingWfhLeavesPerMonth,
          featureLeavesIsActive: userInfo.featureLeavesIsActive,
          featureLeaveReview: userInfo.featureLeaveReview,
          featureLeavesHalfDayOption: userInfo.featureLeavesHalfDayOption,
          featureForm76: userInfo.featureForm76,
          featureColleaguesLeaves: userInfo.featureColleaguesLeaves,
          featureExitInterview: userInfo.featureExitInterview,
          featureSickLeaveWithoutAttachment: userInfo.featureSickLeaveWithoutAttachment,
          featureArt157LeaveWithoutAttachment: userInfo.featureArt157LeaveWithoutAttachment,
          featureLeaveDaysServedTime: userInfo.featureLeaveDaysServedTime,
          featureLeaveDaysIrregularWorkingHours: userInfo.featureLeaveDaysIrregularWorkingHours,
          featureLeaveDaysSpecificWorkingConditions: userInfo.featureLeaveDaysSpecificWorkingConditions,
          featureLeaveDaysAdditionalLeave: userInfo.featureLeaveDaysAdditionalLeave,
          featureHourlyAbsences: userInfo.featureHourlyAbsences,
          featureHealthAndSafetyTraining: userInfo.featureHealthAndSafetyTraining,
          featureWorkTrips: userInfo.featureWorkTrips,
          featureCustomFeatureForClientF: userInfo.featureCustomFeatureForClientF,
          featureLimitWfhLeaveDays: userInfo.featureLimitWfhLeaveDays,
          featureShowWFHLeaveTypeInMainDropdown: userInfo.featureShowWFHLeaveTypeInMainDropdown,
          featureLeaveSubstituteEmployee: userInfo.featureLeaveSubstituteEmployee,
          featureRequireLeaveSubstituteEmployee: userInfo.featureRequireLeaveSubstituteEmployee,
          featureEmployeeCandidatesIsActive: userInfo.featureEmployeeCandidatesIsActive,
          featureGroupRoleLimitsIsActive: userInfo.featureGroupRoleLimitsIsActive,
          featureReminderForProbationPeriodEnd: userInfo.featureReminderForProbationPeriodEnd,
          featureLimitDocumentsToLeaves: userInfo.featureLimitDocumentsToLeaves,
          reminderForIdCardExpiration: userInfo.reminderForIdCardExpiration,
          shouldDisallowLeaveDays: userInfo.shouldDisallowLeaveDays,
          disallowedLeavesFrom: userInfo.disallowedLeavesFrom,
          disallowedLeavesTo: userInfo.disallowedLeavesTo,
          isCandidate: userInfo.isCandidate,
          employeeCandidateHelpText: userInfo.employeeCandidateHelpText,
          totalHourlyAbsencesRemainingHours: userInfo.totalHourlyAbsencesRemainingHours,
          totalHourlyAbsencesUncoveredHours: userInfo.totalHourlyAbsencesUncoveredHours,
          totalHourlyAbsencesHoursAboutToBeCovered: userInfo.totalHourlyAbsencesHoursAboutToBeCovered,
        });

        setWorkMode(workMode);
        setWorkModeName(workModeName);
        setAltWorkModeName(altWorkModeName);

        if (lang) setLanguage(lang);

        setIs2FaComplete(!continueTo2FA);
        setIs2FaEnabled(continueTo2FA);

        // If 2FA flow is Enabled
        if (continueTo2FA) return history.push('/profile/2fa-activate');

        // If 2FA flow is Disabled
        return history.push('/home');
      })
      .catch(err => setServerErrors(createErrorFromResponse(err).errors, methods.setError));
  };
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      btnLabel={t('auth.accountActivation.validate')}
      hideCancelBtn
    >
      <FormGroup
        controlId="formGroupEmail"
        label={t('auth.login.email')}
        type="email"
        name="email"
        formGroupClassName="u-visibility--hidden position-absolute"
      />
      <FormGroup
        controlId="formGroupPassword"
        // label="Парола"
        type="password"
        placeholder={t('auth.accountActivation.pass')}
        name="password"
        hint={t('auth.accountActivation.passHint')}
        isPassword
        isRequired
      />

      <FormGroup
        controlId="formGroupConfirmPassword"
        // label="Повторете парола"
        type="password"
        placeholder={t('auth.accountActivation.confirmPass')}
        name="password_confirmation"
        isPassword
        isConfirmPassword
        isRequired
      />
    </Form>
  );
};

export default ActivateAccountForm;
